<section class="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-10 animate-fade-in relative z-10 pb-20 sm:pb-24 overflow-x-hidden">

  @if (loading()) {
    <div class="card p-6 sm:p-10 rounded-[20px] bg-[#0c0c0c]/80 border border-white/[0.05] text-center">
      <p class="text-text-muted text-[14px]">Loading page content...</p>
    </div>
  } @else if (cms(); as page) {

    <!-- Header -->
    <div class="mb-8 sm:mb-12 text-center max-w-2xl mx-auto">
      <span
        class="inline-block text-gold text-[10px] sm:text-xs uppercase tracking-widest font-extrabold bg-gold/10 px-3 py-1.5 rounded-full border border-gold/25">
        {{ page.badge }}
      </span>
      <h1
        class="font-display font-black text-[26px] sm:text-[36px] md:text-[44px] lg:text-[48px] uppercase tracking-wide text-white mt-4 sm:mt-5 leading-tight break-words">
        {{ page.title }}
        <span class="block sm:inline text-gold text-glow-gold">{{ page.titleHighlight }}</span>
      </h1>
      <p class="text-text-muted text-[13px] sm:text-[14.5px] mt-3 sm:mt-4 leading-relaxed px-1">{{ page.subtitle }}</p>
    </div>

    <!-- Mission -->
    <div
      class="card p-5 sm:p-8 md:p-10 rounded-[16px] sm:rounded-[20px] bg-[#0c0c0c]/80 border border-gold/20 shadow-[0_8px_30px_rgba(0,0,0,0.5)] mb-8 sm:mb-10">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-center">
        <div class="order-2 lg:order-1">
          <h2 class="font-display font-black text-xl sm:text-2xl text-white uppercase tracking-wide mb-4 flex flex-col gap-3">
            {{ page.mission.title }}
            <span class="w-12 h-1 bg-gold rounded-full shadow-[0_0_10px_rgba(245,183,59,0.5)]"></span>
          </h2>
          <p class="text-text-muted text-[13px] sm:text-[14px] leading-relaxed mb-4 break-words">{{ page.mission.paragraph1 }}</p>
          <p class="text-text-muted text-[13px] sm:text-[14px] leading-relaxed break-words">{{ page.mission.paragraph2 }}</p>
        </div>
        <div class="flex justify-center order-1 lg:order-2">
          <img src="/logo.png" alt="DEX"
            class="h-28 w-28 sm:h-40 sm:w-40 object-contain drop-shadow-[0_0_40px_rgba(245,183,59,0.25)]" />
        </div>
      </div>
    </div>

    <!-- Stats -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8 sm:mb-12">
      @for (stat of page.stats; track stat.label) {
        <div
          class="bg-[#0c0c0c]/80 border border-white/[0.05] shadow-[0_12px_45px_rgba(0,0,0,0.4)] rounded-[16px] p-4 sm:p-6 backdrop-blur-xl min-w-0">
          <div class="flex items-center gap-3 text-text-muted mb-3 sm:mb-4 min-w-0">
            <svg class="w-5 h-5 text-gold shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round"
                d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
            <span class="text-[12px] sm:text-[13px] font-bold truncate">{{ stat.label }}</span>
          </div>
          <div class="text-[22px] sm:text-[26px] font-black text-white mb-1 sm:mb-2 tracking-tight break-words">{{ stat.value }}</div>
          <div class="text-[11px] sm:text-[12.5px] font-bold text-gold break-words">{{ stat.change }}</div>
        </div>
      }
    </div>

    <!-- Ecosystem -->
    <div class="mb-8 sm:mb-12">
      <div class="text-center mb-6 sm:mb-8 px-1">
        <h2 class="font-display font-black text-xl sm:text-2xl text-white uppercase tracking-wide flex flex-col items-center gap-3 sm:gap-4">
          {{ page.ecosystem.title }}
          <span class="w-12 sm:w-16 h-1 bg-gold rounded-full shadow-[0_0_10px_rgba(245,183,59,0.5)]"></span>
        </h2>
        <p class="text-text-muted text-[12px] sm:text-[13px] mt-3 sm:mt-4 max-w-xl mx-auto leading-relaxed">{{ page.ecosystem.subtitle }}</p>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
        @for (item of page.ecosystem.items; track item.title) {
          <div
            class="card p-5 sm:p-6 rounded-[16px] bg-[#0c0c0c]/80 border border-white/[0.05] hover:border-gold/25 transition-all duration-300 min-w-0">
            <h3 class="font-display font-bold text-white text-[16px] sm:text-[18px] tracking-wide mb-2 break-words">{{ item.title }}</h3>
            <p class="text-[#999999] text-[13px] sm:text-[14px] leading-relaxed break-words">{{ item.description }}</p>
          </div>
        }
      </div>
    </div>

    <!-- Values -->
    <div class="mb-8 sm:mb-12">
      <div class="text-center mb-6 sm:mb-8">
        <h2 class="font-display font-black text-xl sm:text-2xl text-white uppercase tracking-wide flex flex-col items-center gap-3 sm:gap-4">
          {{ page.values.title }}
          <span class="w-12 sm:w-16 h-1 bg-gold rounded-full shadow-[0_0_10px_rgba(245,183,59,0.5)]"></span>
        </h2>
      </div>

      <div class="card p-5 sm:p-6 md:p-8 rounded-[16px] sm:rounded-[20px] bg-[#0c0c0c]/80 border border-gold/20">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          @for (value of page.values.items; track value.title) {
            <div
              class="flex items-start gap-3 sm:gap-4 min-w-0 p-4 sm:p-5 rounded-xl border border-white/[0.05] bg-black/20">
              <div
                class="w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-gold/40 bg-gold/10 flex items-center justify-center text-gold shrink-0">
                <svg class="w-4 h-4 sm:w-5 sm:h-5" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round"
                    d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
                </svg>
              </div>
              <div class="min-w-0 flex-1">
                <h3 class="font-display font-bold text-white text-[15px] sm:text-[17px] tracking-wide break-words">{{ value.title }}</h3>
                <p class="text-[#999999] text-[13px] sm:text-[14px] mt-1.5 leading-relaxed break-words">{{ value.description }}</p>
              </div>
            </div>
          }
        </div>
      </div>
    </div>

    <!-- Trust -->
    <div class="card p-5 sm:p-6 md:p-8 rounded-[16px] sm:rounded-[20px] bg-[#0c0c0c]/80 border border-gold/20 mb-8 sm:mb-12">
      <div class="text-center mb-6 sm:mb-8 px-1">
        <h2 class="font-display font-black text-lg sm:text-xl text-white uppercase tracking-wide break-words">{{ page.trust.title }}</h2>
      </div>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
        @for (feature of page.trust.items; track feature.title) {
          <div class="flex flex-col items-center text-center min-w-0 px-1">
            <div
              class="w-12 h-12 sm:w-14 sm:h-14 rounded-full border border-gold/40 bg-gold/10 flex items-center justify-center text-gold mb-3 shrink-0">
              <svg class="w-5 h-5 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round"
                  d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
              </svg>
            </div>
            <h3 class="font-display font-bold text-white text-[15px] sm:text-[16px] break-words">{{ feature.title }}</h3>
            <p class="text-[#999999] text-[12px] sm:text-[13px] mt-1.5 leading-relaxed break-words">{{ feature.description }}</p>
          </div>
        }
      </div>
    </div>

    <!-- CTA -->
    <div
      class="promo-card-gold p-5 sm:p-8 md:p-10 rounded-[16px] sm:rounded-[20px] flex flex-col md:flex-row items-center justify-between gap-5 sm:gap-6">
      <div class="text-center md:text-left w-full md:w-auto">
        <h3 class="font-display font-black text-lg sm:text-xl text-white uppercase tracking-wide mb-2 break-words">{{ page.cta.title }}</h3>
        <p class="text-text-muted text-[12px] sm:text-[13px] max-w-md mx-auto md:mx-0 break-words">{{ page.cta.description }}</p>
      </div>
      <div class="flex flex-col sm:flex-row w-full md:w-auto items-stretch sm:items-center justify-center gap-3">
        <button (click)="state.selectNav('swap')"
          class="btn-gold w-full sm:w-auto px-7 py-3 rounded-xl text-[11px] sm:text-[12px] font-bold tracking-widest uppercase text-center">
          {{ page.cta.primaryButton }}
        </button>
        <a routerLink="/contact"
          class="btn-outline w-full sm:w-auto px-7 py-3 rounded-xl text-[11px] sm:text-[12px] font-bold tracking-widest uppercase text-center">
          {{ page.cta.secondaryButton }}
        </a>
      </div>
    </div>
  }

</section>
